import { computed, inject, Injectable, signal } from '@angular/core';
import { CompanyLegalRightCompositeEntity, WithUuid } from '@nk/data-entities-lib';
import { AbstractEntityStateService, EntityLoadingState, EntityState } from '@nk/entity-state-lib';
import { NkToastrService } from '@nk/ui-lib';
import { arrayToDict } from '@nk/utils-lib';
import { getLegalRightDictByEntityUuid } from '../utils/company-legal-right.utils';
import { CompanyLegalRightService } from './company-legal-right.service';

const INITIAL_STATE: EntityState<WithUuid<CompanyLegalRightCompositeEntity>> = {
  entitiesByUuid: {},
  entities: [],
  pagination: {
    limit: 0,
    page: 0,
    totalCount: 0,
  },
};

const LOADING_INITIAL_STATE: EntityLoadingState<CompanyLegalRightStateService> = {};

@Injectable({
  providedIn: 'root',
})
export class CompanyLegalRightStateService extends AbstractEntityStateService<
  WithUuid<CompanyLegalRightCompositeEntity>,
  CompanyLegalRightStateService
> {
  protected override entityState = signal(INITIAL_STATE);
  protected override loadingState = signal(LOADING_INITIAL_STATE);
  public entityStateByEntityUuid = computed(() => getLegalRightDictByEntityUuid(this.state().entities));
  private _companyLegalRightService = inject(CompanyLegalRightService);
  private _toastrService = inject(NkToastrService);

  public fetchCompanyLegalRightComposite(
    companyUuid?: string,
    rightsholderCompanyUuid?: string,
    rightsholderPersonUuid?: string,
    legalRightUuid?: string,
  ): void {
    this.updateLoadings('fetchCompanyLegalRightComposite', true);
    this._companyLegalRightService
      .fetchCompanyLegalRightComposite(companyUuid, rightsholderCompanyUuid, rightsholderPersonUuid, legalRightUuid)
      .subscribe({
        next: (composites) =>
          this.updateState(() => {
            return {
              ...INITIAL_STATE,
              entitiesByUuid: {
                ...arrayToDict(composites, (i) => i.company_legal_right.uuid),
              },
              entities: composites,
              pagination: {
                limit: composites.length,
                page: 0,
                totalCount: composites.length,
              },
            };
          }),
        error: (error) => {
          this._toastrService.error(`${error.message}`, `Failed to fetch company legal right list`, {
            timeOut: 10000,
          });
          this.updateLoadings('fetchCompanyLegalRightComposite', false);
        },
        complete: () => this.updateLoadings('fetchCompanyLegalRightComposite', false),
      });
  }
}
